import { LoaderFunctionArgs, useLoaderData } from "react-router"
import { DocsUiLink as Link } from "~/docs-data/docs-link"
import { H1, H2, P, Section } from "~/components/Typography"
import { DocsBackLink } from "~/components/DocsBackLink"
import { MarkdownBody } from "~/components/MarkdownBody"
import { Card, CardHeader, CardTitle, CardDescription } from "~/components/ui/card"
import {
  getCountryCatalog,
  methodologyEntryForCountry,
  methodologyForCountry,
} from "~/docs-data/helpers"
import { supplySources } from "~/docs-data/concepts"
import { getDocsCatalogForRequest } from "~/server/docs-catalog-store.server"

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const { countries } = await getDocsCatalogForRequest(request)
  const cc = getCountryCatalog(countries, params.country ?? "")
  if (!cc) throw new Response("Country catalog not found", { status: 404 })

  // Scoped to the country on purpose: an entry that exists but doesn't apply
  // to this country's signal is a 404 here, not a page we quietly render.
  const entry = methodologyEntryForCountry(cc.countryCode, params.id ?? "")
  if (!entry) throw new Response("Not found", { status: 404 })

  const applicable = methodologyForCountry(cc.countryCode)
  return {
    cc,
    entry,
    // Related entries are filtered to the same country, so a Norway page never
    // links to methodology that doesn't apply in Norway.
    related: (entry.relatedTo ?? [])
      .map((id) => applicable.find((m) => m.id === id))
      .filter((m): m is NonNullable<typeof m> => Boolean(m)),
    supply: supplySources.filter(
      (s) => s.country === cc.countryCode && entry.appliesTo.includes(s.signal),
    ),
  }
}

export default function CountryMethodologyPage() {
  const { cc, entry, related, supply } = useLoaderData<typeof loader>()

  return (
    <div className="max-w-screen-xl">
      <Section>
        <DocsBackLink fallback={`/docs/datasets/${cc.code}/methodology`} />
        <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3">
          <Link to={`/docs/datasets/${cc.code}/methodology`} variant="inline">
            Methodology
          </Link>
          <span>›</span>
          <span className="truncate">{entry.title}</span>
        </div>
        <H1>{entry.title}</H1>
        <P>{entry.longDescription}</P>
        {entry.body && <MarkdownBody source={entry.body} />}
      </Section>

      {supply.length > 0 && (
        <Section>
          <H2>Where it applies</H2>
          <P className="text-sm text-muted-foreground">
            In {cc.name} this applies to{" "}
            {supply.map((s, i) => (
              <span key={s.id}>
                {i > 0 && (i === supply.length - 1 ? " and " : ", ")}
                <Link to={`/docs/datasets/${cc.code}/supply/${s.id}`} variant="inline">
                  {s.shortName}
                </Link>
              </span>
            ))}
            . Each dataset&apos;s page shows the model that instantiates it.
          </P>
        </Section>
      )}

      {related.length > 0 && (
        <Section>
          <H2>Related</H2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {related.map((m) => (
              <Card key={m.id}>
                <CardHeader>
                  <CardTitle className="text-base">
                    <Link
                      to={`/docs/datasets/${cc.code}/methodology/${m.id}`}
                      variant="inline"
                    >
                      {m.title}
                    </Link>
                  </CardTitle>
                  <CardDescription className="line-clamp-2">{m.shortDescription}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </Section>
      )}
    </div>
  )
}
